import { ReactElement, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';

import { BasicFade } from '@components/animators/fade';
import { Slide } from '@components/animators/slide';
import { EmptyState } from '@components/empty-state';
import { createForm, useCreateFormStore } from '@components/form';
import {
  UnsavedChangesPrompter,
  useDeleteModal,
  useUnsavedChangesModal,
} from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconReplace } from '@components/icons/replace';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import {
  CloseButton,
  forceHideSidebar,
  hideSidebar,
  Sidebar,
  useSidebar,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { TabButton } from '@components/tab-button';
import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';
import { formatNumber } from '@utils/numbers';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import {
  AttributeFieldType,
  AttributeType,
  TravellerAttributeTemplate,
} from 'api/attributes/requests';
import {
  getCompanyTravellersQueryKey,
  useBulkEditCompanyTravellerFields,
} from 'api/company-traveller/hooks';
import { CompanyTraveller } from 'api/company-traveller/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import { useBulkEditTravellerJourneyFields } from 'api/traveller-journeys/hooks';
import { TravellerJourney, TravellerJourneyListItem } from 'api/types';
import { useActiveUsers } from 'api/users/hooks';
import { AttributeFields } from 'components/attribute-fields';
import {
  appendAttributeValues,
  companyTravellerAttributePrefix,
  journeyAttributePrefix,
} from 'ui/authenticated/main/helpers';
import { attributeTextMap } from 'ui/authenticated/settings/pages/attributes/common';
import { FieldCard } from 'ui/authenticated/settings/pages/attributes/field-card';
import { FieldFormSection } from 'ui/authenticated/settings/pages/attributes/sidebars/field-form-shared';

type BulkEditFieldsSidebarField = AttributeType | TravellerAttributeTemplate;

export type BulkEditFieldsSidebarProps = {
  title: string;
  sectionLabel: string;
  sectionDescription: string;
  detailsLabel: string;
  editLabel: string;
  attributes: BulkEditFieldsSidebarField[];
  prefix: string;
  selectedCount: number;
  totalCount: number;
  emptyText: string;
  emptyHelpText: string;
  emptyIcon: ReactElement;
  onSave: (
    values: Record<string, unknown>,
    options: { attributeShortId: string; existingValues: ExistingValues }
  ) => Promise<unknown>;
  isSaving: boolean;
  isLoading: boolean;
};

type ExistingValues = 'replace' | 'add' | 'remove';

const isEmptyFieldValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const existingValuesIcons = {
  add: <IconAddPlus />,
  replace: <IconReplace />,
  remove: <IconTrash />,
};

const buildBulkEditFormData = ({
  values,
  attributeShortId,
  existingValues,
  prefix,
  attributes,
  selectedTravellers,
}: {
  values: Record<string, unknown>;
  attributeShortId: string;
  existingValues: string;
  prefix: string;
  attributes: BulkEditFieldsSidebarField[];
  selectedTravellers: Array<{ shortId: string }>;
}) => {
  const formData = new FormData();
  formData.append('ids', selectedTravellers.map((traveller) => traveller.shortId).join(','));
  formData.append('existing_values', existingValues);
  const key = `${prefix}${attributeShortId}`;
  if (isEmptyFieldValue(values[key])) {
    formData.append(key, '');
  } else {
    appendAttributeValues({
      formData,
      data: values,
      attributes,
      prefix,
    });
  }
  return formData;
};

type LocaleKey = keyof (typeof locale)['en'];

type FieldOverrides = {
  existingValues?: ExistingValues[];
  tabLabels?: Record<ExistingValues, LocaleKey>;
  inputLabels: Partial<Record<ExistingValues, LocaleKey>> & { default?: LocaleKey };
  helpText?: Partial<Record<ExistingValues, LocaleKey>>;
  cardDescription?: LocaleKey;
};

// Only types that deviate appear here. Everything else gets the generic editor:
// a "New <type>" label, no existing-values tabs, and the exact-value info box.
const FIELD_OVERRIDES: Partial<Record<AttributeFieldType, FieldOverrides>> = {
  multi_select: {
    existingValues: ['add', 'replace', 'remove'],
    tabLabels: { add: 'addOptions', replace: 'replaceOptions', remove: 'removeOptions' },
    inputLabels: {
      add: 'optionsToAdd',
      replace: 'optionsToReplace',
      remove: 'optionsToRemove',
    },
    helpText: {
      add: 'optionsAddHelp',
      replace: 'optionsReplaceHelp',
      remove: 'optionsRemoveHelp',
    },
  },
  file_multi: {
    existingValues: ['add', 'replace'],
    tabLabels: { add: 'addFiles', replace: 'replaceFiles', remove: 'removeFiles' },
    inputLabels: { add: 'filesToAdd', replace: 'replaceAllFilesWith' },
    cardDescription: 'fileUploadMulti',
  },
  file: {
    inputLabels: { default: 'newFile' },
    cardDescription: 'fileUploadSingle',
  },
};

const Form = createForm<Record<string, unknown>>();

const BulkEditFieldList = ({
  attributes,
  sectionLabel,
  sectionDescription,
  emptyText,
  emptyHelpText,
  emptyIcon,
  onChoose,
}: {
  attributes: BulkEditFieldsSidebarField[];
  sectionLabel: string;
  sectionDescription: string;
  emptyText: string;
  emptyHelpText: string;
  emptyIcon: ReactElement;
  onChoose: (shortId: string) => void;
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <FieldFormSection label={sectionLabel} description={sectionDescription}>
      <Stack space={4}>
        {attributes.length === 0 ? (
          <EmptyState
            variant="small"
            skipMargin
            icon={emptyIcon}
            header={emptyText}
            content={emptyHelpText}
          />
        ) : (
          <Stack space={2}>
            {attributes.map((attribute) => {
              const cardDescription = FIELD_OVERRIDES[attribute.attributeType]?.cardDescription;
              return (
                <FieldCard
                  key={attribute.shortId}
                  attributeType={attribute.attributeType}
                  title={attribute.title}
                  subtitle={cardDescription ? t(cardDescription) : undefined}
                  onClick={() => {
                    return onChoose(attribute.shortId);
                  }}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </FieldFormSection>
  );
};

const unsavedChangesLocale = {
  en: {
    saveChangesTitle: 'Save changes?',
    hasUnsaved: 'This field has unsaved changes.\nWould you like to save or discard them?',
    saveChanges: 'Update field',
  },
};

type BulkEditFieldEditing = {
  isDirty: boolean;
  submit(): void;
};

const BulkEditField = ({
  attribute,
  prefix,
  detailsLabel,
  editLabel,
  selectedCount,
  totalCount,
  onSave,
  isSaving,
  onEditingChange,
}: {
  attribute: BulkEditFieldsSidebarField;
  prefix: string;
  detailsLabel: string;
  editLabel: string;
  selectedCount: number;
  totalCount: number;
  onSave: BulkEditFieldsSidebarProps['onSave'];
  isSaving: boolean;
  onEditingChange: (editing: BulkEditFieldEditing | null) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const showDeleteModal = useDeleteModal();
  const usersQuery = useActiveUsers();

  const overrides = FIELD_OVERRIDES[attribute.attributeType];
  const availableOptions = overrides?.existingValues;
  const [existingValues, setExistingValues] = useState<ExistingValues>(
    availableOptions?.[0] ?? 'replace'
  );

  const fieldKey = `${prefix}${attribute.shortId}`;
  const isValueEmpty = isEmptyFieldValue(formStore.useFieldValue(fieldKey));
  const isClearing = isValueEmpty && existingValues === 'replace';
  const needsValue = isValueEmpty && !isClearing;

  const getInputLabel = () => {
    const labelKey = overrides?.inputLabels[existingValues] ?? overrides?.inputLabels.default;
    if (labelKey) {
      return t(labelKey);
    }
    return t('newValueLabel', {
      field: attributeTextMap[attribute.attributeType].toLowerCase(),
    });
  };

  const helpTextKey = overrides?.helpText?.[existingValues];
  const tabLabels = overrides?.tabLabels;
  const tabOptions =
    availableOptions && tabLabels
      ? availableOptions.map((choice) => {
          return {
            value: choice,
            label: t(tabLabels[choice]),
            icon: existingValuesIcons[choice],
            isDanger: choice === 'remove',
          };
        })
      : undefined;

  const handleSubmit = (data: Record<string, unknown>) => {
    const save = () => {
      return onSave(
        { [fieldKey]: data[fieldKey] ?? '' },
        { attributeShortId: attribute.shortId, existingValues }
      );
    };
    if (isClearing) {
      return showDeleteModal({
        title: t('clearTitle'),
        description:
          selectedCount === 1
            ? t('clearDescriptionOne', { field: attribute.title })
            : t('clearDescription', { field: attribute.title, count: selectedCount }),
        buttonText: t('clearButton'),
        onConfirm: save,
      });
    }
    return save();
  };

  const isDirty = formStore.useIsDirty();
  const submit = useEvent(() => {
    return formStore.actions.submit({
      onSubmit: () => handleSubmit(formStore.getState().values),
      action: 'submit',
    });
  });

  useEffect(() => {
    onEditingChange({ isDirty, submit });
    return () => onEditingChange(null);
  }, [isDirty, onEditingChange, submit]);

  return (
    <Form store={formStore} onSubmit={handleSubmit}>
      <UnsavedChangesPrompter onSubmit={handleSubmit} locale={unsavedChangesLocale} />
      <Stack space={6}>
        <FieldFormSection label={detailsLabel}>
          <Stack space={4}>
            <InfoBox
              variant="indigo"
              icon={<IconFormIndicatorInfo color="elementIndigo100" />}
              text={t('selectionSummary', {
                selected: formatNumber(selectedCount),
                total: formatNumber(totalCount),
              })}
            />
            <FieldCard
              attributeType={attribute.attributeType}
              title={attribute.title}
              subtitle={overrides?.cardDescription ? t(overrides.cardDescription) : undefined}
            />
          </Stack>
        </FieldFormSection>

        <Sidebar.Separator />

        <FieldFormSection label={editLabel}>
          <Stack space={4}>
            {tabOptions && (
              <TabButton<ExistingValues>
                size="md"
                fullWidth
                value={existingValues}
                onChange={setExistingValues}
                options={tabOptions}
              />
            )}
            <AttributeFields
              attributeTemplate={{ ...attribute, isRequired: false }}
              prefix={prefix}
              label={getInputLabel()}
              description={helpTextKey ? t(helpTextKey) : undefined}
              placeholder={t('addValue')}
              usersData={usersQuery.data}
            />
            {!availableOptions && (
              <InfoBox
                variant="indigo"
                icon={<IconFormIndicatorInfo color="elementIndigo100" />}
                text={t('exactValueHint')}
              />
            )}
          </Stack>
        </FieldFormSection>
      </Stack>
      <Sidebar.Footer>
        <Form.Actions>
          <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
          <Form.Submit
            text={t('updateField')}
            variant="primary-blue"
            isDisabled={needsValue || isSaving}
            dataTestid="bulk-edit-save"
          />
        </Form.Actions>
      </Sidebar.Footer>
    </Form>
  );
};

export const BulkEditFieldsSidebar = (props: BulkEditFieldsSidebarProps) => {
  const { title, attributes, isLoading, ...rest } = props;
  const { t } = useScopedTranslate(locale);
  const [selectedShortId, setSelectedShortId] = useState<string>();
  const [editing, setEditing] = useState<BulkEditFieldEditing | null>(null);
  const showUnsavedChanges = useUnsavedChangesModal();

  const selectedAttribute = attributes.find((attribute) => attribute.shortId === selectedShortId);

  const goBack = () => {
    return setSelectedShortId(undefined);
  };

  const handleBack = () => {
    if (!editing?.isDirty) {
      return goBack();
    }
    return showUnsavedChanges({ onSave: editing.submit, onDiscard: goBack });
  };

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            <AnimatePresence initial={false}>
              {selectedAttribute && (
                <BasicFade>
                  <IconButton
                    icon={<IconArrowLeft />}
                    variant="ghost-purple"
                    tooltip={t('core.back')}
                    aria-label={t('core.back')}
                    dataTestid="bulk-edit-back"
                    onClick={handleBack}
                  />
                </BasicFade>
              )}
            </AnimatePresence>
            <IconEdit color="elementBlue100" />
            <Sidebar.HeaderTitle title={title} />
          </Stack>
          <CloseButton />
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        {isLoading ? (
          <Sidebar.Spinner />
        ) : (
          <AnimatePresence initial={false} exitBeforeEnter>
            {selectedAttribute ? (
              <Slide key="bulk-edit-field" offset={30}>
                <BulkEditField
                  attribute={selectedAttribute}
                  prefix={rest.prefix}
                  detailsLabel={rest.detailsLabel}
                  editLabel={rest.editLabel}
                  selectedCount={rest.selectedCount}
                  totalCount={rest.totalCount}
                  onSave={rest.onSave}
                  isSaving={rest.isSaving}
                  onEditingChange={setEditing}
                />
              </Slide>
            ) : (
              <Slide key="bulk-edit-field-list" offset={-30}>
                <BulkEditFieldList
                  attributes={attributes}
                  sectionLabel={rest.sectionLabel}
                  sectionDescription={rest.sectionDescription}
                  emptyText={rest.emptyText}
                  emptyHelpText={rest.emptyHelpText}
                  emptyIcon={rest.emptyIcon}
                  onChoose={setSelectedShortId}
                />
              </Slide>
            )}
          </AnimatePresence>
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

export const useBulkEditJourneyFieldsSidebar = () =>
  useSidebar(BulkEditJourneyFieldsSidebar, { variant: 'CONTENT_HEAVY', hasBackdrop: true });

type BulkEditJourneyFieldsSidebarProps = {
  journeyId: string;
  selectedTravellers: Array<TravellerJourney | TravellerJourneyListItem>;
};

const BulkEditJourneyFieldsSidebar = ({
  journeyId,
  selectedTravellers,
}: BulkEditJourneyFieldsSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const attributesQuery = useJourneyAttributes(journeyId);
  const totalCount = (queryClient.getQueryData<unknown[]>(['travellers', journeyId]) ?? []).length;
  const attributes = (attributesQuery.data ?? []).filter((attribute) => attribute.isActive);

  const mutation = useBulkEditTravellerJourneyFields(journeyId, {
    onSuccess: () => {
      forceHideSidebar();
    },
  });

  const onSave = (
    values: Record<string, unknown>,
    { attributeShortId, existingValues }: { attributeShortId: string; existingValues: string }
  ) => {
    const formData = buildBulkEditFormData({
      values,
      attributeShortId,
      existingValues,
      prefix: journeyAttributePrefix,
      attributes,
      selectedTravellers,
    });
    return mutation.mutateAsync(formData);
  };

  return (
    <BulkEditFieldsSidebar
      title={t('bulkEditFieldValues')}
      sectionLabel={t('fields')}
      sectionDescription={t('fieldsDescription')}
      detailsLabel={t('details')}
      editLabel={t('editField')}
      attributes={attributes}
      prefix={journeyAttributePrefix}
      selectedCount={selectedTravellers.length}
      totalCount={totalCount}
      emptyText={t('noJourneyFields')}
      emptyHelpText={t('noJourneyFieldsHelp')}
      emptyIcon={<IconAttributeJourney />}
      onSave={onSave}
      isSaving={mutation.isLoading}
      isLoading={attributesQuery.isLoading}
    />
  );
};

export const useBulkEditCompanyTravellerFieldsSidebar = () =>
  useSidebar(BulkEditCompanyTravellerFieldsSidebar, {
    variant: 'CONTENT_HEAVY',
    hasBackdrop: true,
  });

type BulkEditCompanyTravellerFieldsSidebarProps = {
  selectedTravellers: CompanyTraveller[];
};

export const BulkEditCompanyTravellerFieldsSidebar = ({
  selectedTravellers,
}: BulkEditCompanyTravellerFieldsSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const attributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const totalCount = (
    queryClient.getQueryData<unknown[]>(getCompanyTravellersQueryKey('full')) ?? []
  ).length;
  const attributes = (attributeTemplatesQuery.data ?? [])
    .filter((template) => template.isActive)
    .map((template) => ({ ...template, title: template.templateTitle }));

  const mutation = useBulkEditCompanyTravellerFields({
    onSuccess: () => {
      forceHideSidebar();
    },
  });

  const onSave = (
    values: Record<string, unknown>,
    { attributeShortId, existingValues }: { attributeShortId: string; existingValues: string }
  ) => {
    const formData = buildBulkEditFormData({
      values,
      attributeShortId,
      existingValues,
      prefix: companyTravellerAttributePrefix,
      attributes,
      selectedTravellers,
    });
    return mutation.mutateAsync(formData);
  };

  return (
    <BulkEditFieldsSidebar
      title={t('bulkEditFieldValues')}
      sectionLabel={t('fields')}
      sectionDescription={t('fieldsDescription')}
      detailsLabel={t('details')}
      editLabel={t('editField')}
      attributes={attributes}
      prefix={companyTravellerAttributePrefix}
      selectedCount={selectedTravellers.length}
      totalCount={totalCount}
      emptyText={t('noTravellerFields')}
      emptyHelpText={t('noTravellerFieldsHelp')}
      emptyIcon={<IconAttributeTraveller />}
      onSave={onSave}
      isSaving={mutation.isLoading}
      isLoading={attributeTemplatesQuery.isLoading}
    />
  );
};

const locale = {
  en: {
    updateField: 'Update field',
    addValue: 'New value...',
    selectionSummary: '{selected} of {total} travellers selected',
    exactValueHint: 'Every selected record gets this exact value',
    newValueLabel: 'New {field}',
    newFile: 'New file',
    fields: 'Fields',
    fieldsDescription: 'Pick a field to change',
    details: 'Details',
    editField: 'Edit field',
    addOptions: 'Add',
    replaceOptions: 'Replace',
    removeOptions: 'Remove',
    addFiles: 'Add files',
    replaceFiles: 'Replace files',
    removeFiles: 'Remove files',
    optionsToAdd: 'Options to add',
    optionsToReplace: 'Options to replace',
    optionsToRemove: 'Options to remove',
    filesToAdd: 'Files to add',
    replaceAllFilesWith: 'Replace all files with',
    optionsAddHelp: 'These options are added, existing options stay',
    optionsReplaceHelp: 'Existing options are cleared and replaced with selected options',
    optionsRemoveHelp: 'Selected options are removed where present',
    fileUploadSingle: 'File upload - Single',
    fileUploadMulti: 'File upload - Multi',
    clearTitle: 'Clear this field?',
    clearDescription: 'This removes the value of {field} for {count} travellers.',
    clearDescriptionOne: 'This removes the value of {field} for 1 traveller.',
    clearButton: 'Clear value',
    bulkEditFieldValues: 'Bulk edit field values',
    noJourneyFields: 'No journey fields',
    noJourneyFieldsHelp: 'This journey has no fields configured',
    noTravellerFields: 'No traveller fields',
    noTravellerFieldsHelp: 'This company has no traveller fields configured',
  },
};
